import { useState } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { AuditLedger } from '@/components/security/AuditLedger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuditLog } from '@/hooks/useAuditLog';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  ScrollText, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  User,
  Calendar,
  Filter,
  Download,
  Lock,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import type { AuditLogEntry, AuditAction } from '@/lib/security/types';

const ACTION_LABELS: Record<AuditAction, string> = {
  case_created: 'Case Created',
  case_approved: 'Case Approved',
  case_rejected: 'Case Rejected',
  submission_initiated: 'Submission Started',
  submission_completed: 'Submission Completed',
  submission_failed: 'Submission Failed',
  status_changed: 'Status Changed',
  document_uploaded: 'Document Uploaded',
  document_removed: 'Document Removed',
  override_applied: 'Override Applied',
  export_generated: 'Export Generated',
  epic_pushback_sent: 'Epic Pushback Sent',
  epic_pushback_failed: 'Epic Pushback Failed',
  sensitive_data_viewed: 'Sensitive Data Viewed',
  user_login: 'User Login',
  user_logout: 'User Logout',
  permission_changed: 'Permission Changed',
  config_changed: 'Config Changed',
};

const ACTION_COLORS: Record<AuditAction, string> = {
  case_created: 'bg-blue-500/10 text-blue-500',
  case_approved: 'bg-primary/10 text-primary',
  case_rejected: 'bg-red-500/10 text-red-500',
  submission_initiated: 'bg-indigo-500/10 text-indigo-500',
  submission_completed: 'bg-primary/10 text-primary',
  submission_failed: 'bg-red-500/10 text-red-500',
  status_changed: 'bg-amber-500/10 text-amber-500',
  document_uploaded: 'bg-cyan-500/10 text-cyan-500',
  document_removed: 'bg-orange-500/10 text-orange-500',
  override_applied: 'bg-purple-500/10 text-purple-500',
  export_generated: 'bg-slate-500/10 text-slate-400',
  epic_pushback_sent: 'bg-blue-500/10 text-blue-500',
  epic_pushback_failed: 'bg-red-500/10 text-red-500',
  sensitive_data_viewed: 'bg-amber-500/10 text-amber-500',
  user_login: 'bg-slate-500/10 text-slate-400',
  user_logout: 'bg-slate-500/10 text-slate-400',
  permission_changed: 'bg-purple-500/10 text-purple-500',
  config_changed: 'bg-orange-500/10 text-orange-500',
};

function AuditLogRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b last:border-0">
      <div 
        className="flex items-center gap-4 p-4 hover:bg-muted/50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className={ACTION_COLORS[entry.action]}>
              {ACTION_LABELS[entry.action]}
            </Badge>
            {entry.phi_accessed && (
              <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                <Eye className="w-3 h-3 mr-1" />
                PHI
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {entry.resource_type}
            {entry.resource_id && ` • ${entry.resource_id.slice(0, 8)}...`}
          </p>
        </div>
        <div className="text-right text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <User className="w-3 h-3" />
            {entry.user_email || entry.user_id.slice(0, 8)}
          </div>
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <Calendar className="w-3 h-3" />
            {format(new Date(entry.created_at), 'PPp')}
          </div>
        </div>
      </div>
      {expanded && Object.keys(entry.details || {}).length > 0 && (
        <div className="px-4 pb-4">
          <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function AuditLogPage() {
  const { hasPermission, clientId, isSuperAdmin } = usePermissions();
  const [filters, setFilters] = useState({
    action: '' as AuditAction | '',
    resourceType: '',
    startDate: '',
    endDate: '',
  });

  const { logs, total, page, pageCount, isLoading, nextPage, prevPage } = useAuditLog({
    filters: {
      clientId: isSuperAdmin() ? undefined : clientId || undefined,
      action: filters.action || undefined,
      resourceType: filters.resourceType || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    },
    enabled: hasPermission('audit.view'),
  });

  if (!hasPermission('audit.view')) {
    return (
      <OpsLayout>
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-2">Audit Log</h1>
          <p className="text-muted-foreground mb-6">Access denied</p>
          <Card>
            <CardContent className="py-12 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">You don't have permission to view audit logs.</p>
            </CardContent>
          </Card>
        </div>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-2">Audit Log</h1>
        <p className="text-muted-foreground mb-6">Immutable record of all system actions</p>

        {/* Cryptographic Audit Ledger */}
        <div className="mb-6">
          <AuditLedger />
        </div>
        
        <div className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Action Type</Label>
                  <Select
                    value={filters.action}
                    onValueChange={v => setFilters(prev => ({ ...prev, action: v as AuditAction }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All actions</SelectItem>
                      {Object.entries(ACTION_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Resource Type</Label>
                  <Input
                    placeholder="e.g., authorization_request"
                    value={filters.resourceType}
                    onChange={e => setFilters(prev => ({ ...prev, resourceType: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Log List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ScrollText className="w-5 h-5" />
                    Audit Entries
                  </CardTitle>
                  <CardDescription>
                    Showing {logs.length} of {total} entries
                  </CardDescription>
                </div>
                {hasPermission('audit.export') && (
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Loading audit logs...
                </div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  No audit entries found matching your filters.
                </div>
              ) : (
                <div className="divide-y">
                  {logs.map(entry => (
                    <AuditLogRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {pageCount}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={prevPage}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={nextPage}
                  disabled={page >= pageCount - 1}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </OpsLayout>
  );
}
